/**
 * Global typing queue to ensure only one message types at a time
 */
let typingQueue = Promise.resolve();
let isGlobalTypingActive = false;

import { setupQuiz } from './questionQuiz';
import { hideMessageLoader } from './hideMessageLoader';
import { scrollIfAllowed } from './scrollIfAllowed';
import { scrollToChatBottom } from './scrollToChatBottom';
import { chatDebugLog } from '../helpers/chatDebug';

/**
 * Get the current typing queue promise
 * This allows external code to wait for all typing to complete
 */
export function getTypingQueue() {
    return typingQueue;
}

export function isTypingActive() {
    return isGlobalTypingActive;
}

/**
 * Typing animation for chat messages
 * Animates text character by character to simulate typing effect
 * 
 * @param {jQuery} messageElement - The message element to animate
 * @param {number} speed - Speed of typing in milliseconds per character (default: 30)
 * @returns {Promise} - Resolves when animation is complete
 */
function waitForImages(element) {
    const images = element.find('img');
    if (images.length === 0) return Promise.resolve();

    const promises = images.map(function () {
        return new Promise((resolve) => {
            if (this.complete) {
                resolve();
            } else {
                $(this).on('load error', resolve);
            }
        });
    }).get();

    return Promise.all(promises);
}

export function typingAnimation(messageElement, speed = 30) {
    return new Promise((resolve) => {
        // Try both possible selectors for content
        let contentWrapper = messageElement.find('.g-chatform-message-content-wrapper');

        // If wrapper doesn't exist, try the inner content directly
        if (contentWrapper.length === 0) {
            contentWrapper = messageElement.find('.g-chatform-message-content-inner');
        }

        if (contentWrapper.length === 0) {
            resolve();
            return;
        }

        // Get all text nodes and elements that need to be animated
        const elementsToAnimate = [];

        // Process all child elements
        contentWrapper.children().each(function () {
            const element = $(this);

            // Skip images, audio, and other non-text elements
            if (element.is('img, audio, video, .audio-message, .g-chatform-message-img-wrapper, .g-chatform-message-assets-wrapper')) {
                elementsToAnimate.push({
                    type: 'element',
                    element: element,
                    html: element[0].outerHTML
                });
            } else {
                // For text elements (p, div, etc.), we'll animate the text
                elementsToAnimate.push({
                    type: 'text',
                    element: element,
                    html: element.html(),
                    text: element.text()
                });
            }
        });

        // If no elements to animate, resolve immediately
        if (elementsToAnimate.length === 0) {
            resolve();
            return;
        }

        // Cleanly hide loader and reset pending state as typing starts
        hideMessageLoader(messageElement);

        // Clear the content wrapper
        contentWrapper.empty();

        let currentElementIndex = 0;
        let currentCharIndex = 0;
        let currentElement = null;
        let currentText = '';
        const totalLength = elementsToAnimate.reduce((acc, curr) => acc + (curr.text ? curr.text.length : 1), 0);
        let charactersTyped = 0;

        function typeNextCharacter() {
            if (currentElementIndex >= elementsToAnimate.length) {
                // Final cleanup of pending states after message is fully typed
                setTimeout(() => {
                    messageElement.removeClass('is-new-message');
                    hideMessageLoader(messageElement);
                }, 500);
                isGlobalTypingActive = false;
                resolve();
                return;
            }

            const item = elementsToAnimate[currentElementIndex];

            // If this is a new element, create it
            if (currentElement === null) {
                if (item.type === 'element') {
                    // For non-text elements, just append them immediately
                    contentWrapper.append(item.html);
                    currentElementIndex++;
                    currentElement = null;
                    currentCharIndex = 0;
                    setTimeout(typeNextCharacter, speed);
                    return;
                } else {
                    // Create the element structure
                    currentElement = $('<' + item.element.prop('tagName').toLowerCase() + '>');

                    // Copy attributes
                    $.each(item.element[0].attributes, function () {
                        currentElement.attr(this.name, this.value);
                    });

                    contentWrapper.append(currentElement);
                    currentText = item.text;
                    currentCharIndex = 0;
                }
            }

            // Type the next character
            if (currentCharIndex < currentText.length) {
                const char = currentText[currentCharIndex];
                currentElement.text(currentElement.text() + char);
                currentCharIndex++;
                charactersTyped++;
                scrollIfAllowed();

                // Calculate variable delay for human-like speed curve
                // Start slow (0-20% length), speed up in middle, slow down at very end
                const progress = charactersTyped / totalLength;
                let dynamicDelay = speed;

                if (progress < 0.2) {
                    dynamicDelay = speed * 1.5; // 50% slower at start
                } else if (progress > 0.8) {
                    dynamicDelay = speed * 1.2; // 20% slower at end
                } else {
                    dynamicDelay = speed * 0.8; // 20% faster in middle
                }

                // Add small random jitter (±5ms)
                dynamicDelay += (Math.random() * 10 - 5);

                setTimeout(typeNextCharacter, Math.max(10, dynamicDelay));
            } else {
                // Finished this element, move to next
                currentElementIndex++;
                currentElement = null;
                currentCharIndex = 0;
                setTimeout(typeNextCharacter, speed);
            }
        }

        // Start typing animation
        typeNextCharacter();
    });
}

/**
 * Enhanced typing animation that handles HTML content properly
 * Preserves HTML tags while animating text content
 * 
 * @param {jQuery} messageElement - The message element to animate
 * @param {number} speed - Speed of typing in milliseconds per character (default: 20)
 * @returns {Promise} - Resolves when animation is complete
 */
export function typingAnimationHTML(messageElement, speed = 20) {

    // Add to queue to ensure sequential typing
    const queuedAnimation = typingQueue.then(() => {
        isGlobalTypingActive = true;
        return new Promise((resolve) => {
            const emojiSelector = 'img.emoji, img.wp-smiley';
            const mediaSelector = 'img:not(.emoji):not(.wp-smiley), audio, video, .audio-message, .g-chatform-message-img-wrapper, .g-chatform-message-assets-wrapper, .gift-wrapper';
            const revealAfterTypingSelector = '[data-typing-reveal]';
            const hiddenContainerSelector = `.audio-message, .g-chatform-message-img-wrapper, .g-chatform-message-assets-wrapper, .gift-wrapper, audio, video, ${revealAfterTypingSelector}`;

            // Try both possible selectors for content
            let contentWrapper = messageElement.find('.g-chatform-message-content-wrapper');

            // If wrapper doesn't exist, try the inner content directly
            if (contentWrapper.length === 0) {
                contentWrapper = messageElement.find('.g-chatform-message-content-inner');
            }

            // Hide message actions initially (menu icon, delete, etc.)
            const actions = messageElement.find('.g-chatform-message-actions');
            actions.hide();

            chatDebugLog('chatContent', '[chat-debug] typingAnimationHTML:start', {
                role: messageElement.attr('data-role'),
                messageType: messageElement.attr('data-message-type'),
                rawText: messageElement.attr('data-raw-text') || '',
                hasQuizChips: contentWrapper.find('.g-chat-quiz-chips').length > 0,
                holderStyleBefore: messageElement.find('.g-chatform-message-inner').attr('style') || '',
                contentStyleBefore: contentWrapper.attr('style') || '',
            });

            // Hide loader as soon as typing starts
            hideMessageLoader(messageElement);

            // Some server-rendered messages arrive with inline display:none on the
            // shell wrappers. Clear that now so the bubble can appear exactly when
            // the typing animation starts, without flashing the full final content.
            messageElement.show();
            messageElement.find('.g-chatform-message-inner').show();

            chatDebugLog('chatContent', '[chat-debug] typingAnimationHTML:shell-shown', {
                holderStyleAfterShow: messageElement.find('.g-chatform-message-inner').attr('style') || '',
                contentStyleAfterShow: contentWrapper.attr('style') || '',
            });

            if (contentWrapper.length === 0) {
                console.warn('typingAnimationHTML: contentWrapper not found');
                resolve();
                return;
            }

            // Clean up any empty paragraphs or elements that might have been generated by formatting
            // NOTE: Skip elements inside interactive selectors (quiz, form) — input wrappers have no
            // text content and would be wrongly deleted by this cleanup.
            const interactiveSelectorForCleanup = `.growtype-quiz-wrapper, form, .quiz, .poll, ${revealAfterTypingSelector}`;
            contentWrapper.find('p, div, blockquote, span, h1, h2, h3, h4, h5, h6').each(function () {
                const $el = $(this);

                // Never remove elements that are inside a quiz/form wrapper
                if ($el.closest(interactiveSelectorForCleanup).length > 0) {
                    return;
                }

                // Check if it's truly empty (no text and no tags/assets)
                const hasText = $el.text().trim().length > 0;
                const hasMedia = $el.find('img, video, audio, .audio-message, .growtype-quiz-wrapper, .gift-wrapper, iframe, canvas, svg').length > 0;

                if (!hasText && !hasMedia) {
                    $el.remove();
                }
            });

            // Check if message contains non-text elements (images, audio, video, etc.)
            const hasMedia = contentWrapper.find(mediaSelector).length > 0;

            // Check if message contains interactive elements (quizzes, polls, forms)
            const interactiveSelector = `.growtype-quiz-wrapper, form, .quiz, .poll, button, ${revealAfterTypingSelector}`;
            const interactiveElements = contentWrapper.find(interactiveSelector);
            const hasInteractive = interactiveElements.length > 0;

            // Extract text content excluding interactive elements and media
            const tempDiv = $('<div>').html(contentWrapper.html());
            tempDiv.find(`.growtype-quiz-wrapper, form, .quiz, .poll, button, ${revealAfterTypingSelector}, ${mediaSelector}`).remove();
            const textContent = tempDiv.text().trim();

            chatDebugLog('chatContent', '[chat-debug] typingAnimationHTML:content-analysis', {
                hasMedia,
                hasInteractive,
                interactiveCount: interactiveElements.length,
                textLength: textContent.length,
                htmlPreview: contentWrapper.html()?.slice(0, 300) || '',
            });


            // Adjust typing speed based on length to make long messages appear faster
            if (textContent.length > 100) speed = 18;
            if (textContent.length > 300) speed = 15;
            if (textContent.length > 600) speed = 10;

            // PRE-ANIMATION: Prepare semantic structure
            const originalHTML = contentWrapper.html();
            const typingSequence = [];

            // Define interactive elements that should appear immediately (not typed)
            // The interactiveSelector is already defined above.

            const walk = document.createTreeWalker(contentWrapper[0], NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT, {
                acceptNode: function (node) {
                    if (node.nodeType === Node.TEXT_NODE) {
                        const parentElement = node.parentElement;

                        if (!parentElement) {
                            return NodeFilter.FILTER_REJECT;
                        }

                        if ($(parentElement).closest(interactiveSelector).length || $(parentElement).closest(hiddenContainerSelector).length) {
                            return NodeFilter.FILTER_REJECT;
                        }

                        return node.nodeValue.length > 0 ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
                    }

                    if (node.nodeType === Node.ELEMENT_NODE && node.matches(emojiSelector)) {
                        if ($(node).closest(interactiveSelector).length || $(node).closest(hiddenContainerSelector).length) {
                            return NodeFilter.FILTER_REJECT;
                        }

                        return NodeFilter.FILTER_ACCEPT;
                    }

                    return NodeFilter.FILTER_SKIP;
                }
            }, false);

            let node;
            while (node = walk.nextNode()) {
                if (node.nodeType === Node.TEXT_NODE) {
                    typingSequence.push({
                        type: 'text',
                        node: node,
                        originalText: node.nodeValue
                    });
                    node.nodeValue = '';
                } else if (node.nodeType === Node.ELEMENT_NODE) {
                    typingSequence.push({
                        type: 'emoji',
                        element: $(node)
                    });
                }
            }

            chatDebugLog('chatContent', '[chat-debug] typingAnimationHTML:sequence-built', {
                sequenceLength: typingSequence.length,
                firstItems: typingSequence.slice(0, 10).map((item) => (
                    item.type === 'text'
                        ? { type: item.type, preview: (item.originalText || '').slice(0, 40) }
                        : { type: item.type, tag: item.element?.prop('tagName') || '' }
                )),
            });

            // Hide media elements so they don't show before typing finishes (using hide to avoid empty space)
            const mediaElements = contentWrapper.find(mediaSelector);
            mediaElements.hide().css('visibility', 'hidden');

            // Hide inline emoji so they can be revealed in typing order
            const inlineEmojiElements = contentWrapper.find(emojiSelector);
            inlineEmojiElements.css('visibility', 'hidden');

            // Hide interactive elements initially (using hide to avoid empty space)
            interactiveElements.hide();

            // NEW: Hide all block elements (p, div, etc.) inside the bubble initially
            const styleContainers = contentWrapper.find('p, div, blockquote, h1, h2, h3, h4, h5, h6').filter(function () {
                return $(this).closest(interactiveSelector).length === 0;
            });
            styleContainers.hide();

            chatDebugLog('chatContent', '[chat-debug] typingAnimationHTML:pre-animation-hidden', {
                mediaCount: mediaElements.length,
                emojiCount: inlineEmojiElements.length,
                interactiveCount: interactiveElements.length,
                styleContainerCount: styleContainers.length,
                holderStyleHiddenPhase: messageElement.find('.g-chatform-message-inner').attr('style') || '',
                contentStyleHiddenPhase: contentWrapper.attr('style') || '',
            });

            contentWrapper.show();

            let currentNodeIndex = 0;
            let currentCharIndex = 0;

            function typeNextChar() {
                if (currentNodeIndex < typingSequence.length) {
                    const item = typingSequence[currentNodeIndex];

                    if (item.type === 'emoji') {
                        item.element.css('visibility', 'visible');
                        currentNodeIndex++;
                        scrollIfAllowed();
                        setTimeout(typeNextChar, Math.max(10, speed * 0.75));
                        return;
                    }

                    const text = item.originalText;

                    // Show ALL parents of the text node up to the content wrapper
                    if (currentCharIndex === 0) {
                        $(item.node).parentsUntil(contentWrapper).show();
                        chatDebugLog('chatContent', '[chat-debug] typingAnimationHTML:node-start', {
                            nodeIndex: currentNodeIndex,
                            textPreview: text.slice(0, 80),
                        });
                    }

                    if (currentCharIndex < text.length) {
                        const char = text[currentCharIndex];
                        item.node.nodeValue += char;
                        currentCharIndex++;
                        scrollIfAllowed();

                        // Natural jitter
                        let charDelay = speed + Math.floor(Math.random() * 5);
                        setTimeout(typeNextChar, charDelay);
                    } else {
                        currentNodeIndex++;
                        currentCharIndex = 0;
                        setTimeout(typeNextChar, speed);
                    }
                } else {
                    // TYPING FINISHED
                    const shouldRestoreOriginalHtml = hasMedia || hasInteractive;

                    chatDebugLog('chatContent', '[chat-debug] typingAnimationHTML:typing-finished', {
                        shouldRestoreOriginalHtml,
                        hasMedia,
                        hasInteractive,
                    });

                    if (shouldRestoreOriginalHtml) {
                        contentWrapper.html(originalHTML); // Restore full HTML only when hidden rich content must be reattached.
                        chatDebugLog('chatContent', '[chat-debug] typingAnimationHTML:html-restored', {
                            restoredHtmlPreview: contentWrapper.html()?.slice(0, 300) || '',
                        });
                    } else {
                        contentWrapper.find(emojiSelector).css('visibility', 'visible');
                    }

                    // Identify rich elements to reveal only after typing has finished.
                    const elementsToFadeSelector = `${mediaSelector}, .growtype-quiz-wrapper, form, .quiz, .poll`;
                    const elementsToFade = contentWrapper.find(elementsToFadeSelector).filter(function () {
                        return $(this).closest(revealAfterTypingSelector).length === 0;
                    });
                    const elementsToSlide = contentWrapper.find(revealAfterTypingSelector);

                    chatDebugLog('chatContent', '[chat-debug] typingAnimationHTML:elements-to-fade', {
                        fadeCount: elementsToFade.length,
                        slideCount: elementsToSlide.length,
                        hasQuizChipsAfterRestore: contentWrapper.find('.g-chat-quiz-chips').length > 0,
                    });

                    const revealAnimationCount = elementsToFade.length + elementsToSlide.length;
                    let completedRevealAnimations = 0;
                    const completeRevealAnimation = () => {
                        completedRevealAnimations++;
                        if (completedRevealAnimations === revealAnimationCount) {
                            chatDebugLog('chatContent', '[chat-debug] typingAnimationHTML:fade-complete', {
                                hasQuizChipsAfterFade: contentWrapper.find('.g-chat-quiz-chips').length > 0,
                                holderStyleAfterFade: messageElement.find('.g-chatform-message-inner').attr('style') || '',
                                contentStyleAfterFade: contentWrapper.attr('style') || '',
                            });
                            // Re-init interactive components
                            contentWrapper.find('.growtype-quiz-wrapper').each(function () {
                                setupQuiz($(this));
                            });
                            scrollToChatBottom(400, true);
                            isGlobalTypingActive = false;
                            resolve();
                        }
                    };

                    if (elementsToFade.length > 0) {
                        // Hide again immediately after restoring HTML, then reveal as one rich element.
                        elementsToFade.hide().css('visibility', 'visible');
                        elementsToFade.fadeIn(400, completeRevealAnimation);
                    }

                    if (elementsToSlide.length > 0) {
                        // Purchase/offer blocks should arrive as a unit, never character by character.
                        elementsToSlide.hide().css('visibility', 'visible');
                        elementsToSlide.slideDown(320, completeRevealAnimation);
                    }

                    // Reveal message actions (menu, delete)
                    const actions = messageElement.find('.g-chatform-message-actions');
                    if (actions.length > 0) {
                        actions.fadeIn(400);
                    }

                    if (revealAnimationCount === 0) {
                        chatDebugLog('chatContent', '[chat-debug] typingAnimationHTML:complete-no-fade', {
                            hasQuizChipsAtEnd: contentWrapper.find('.g-chat-quiz-chips').length > 0,
                            holderStyleAtEnd: messageElement.find('.g-chatform-message-inner').attr('style') || '',
                            contentStyleAtEnd: contentWrapper.attr('style') || '',
                        });
                        isGlobalTypingActive = false;
                        resolve();
                    }
                }
            }

            typeNextChar();
        });
    });

    // Update the queue
    typingQueue = queuedAnimation;

    return queuedAnimation;
}

/**
 * Simple typing animation without HTML parsing
 * Just types plain text character by character
 * 
 * @param {jQuery} element - The element to animate
 * @param {string} text - The text to type
 * @param {number} speed - Speed of typing in milliseconds per character
 * @returns {Promise} - Resolves when animation is complete
 */
export function simpleTypingAnimation(element, text, speed = 30) {
    return new Promise((resolve) => {
        element.empty();
        let index = 0;

        function typeNextChar() {
            if (index < text.length) {
                const char = text[index];
                element.text(element.text() + char);
                index++;
                scrollIfAllowed();

                // Use a consistent speed without punctuation pauses
                let charDelay = speed + Math.floor(Math.random() * 5);
                setTimeout(typeNextChar, charDelay);
            } else {
                resolve();
            }
        }

        typeNextChar();
    });
}
